import type { PipelineTrial } from "@/lib/recon/pipeline-types"

export type StatusMeta = { varName: string; label: string }

export const STATUS: Record<string, StatusMeta> = {
  RECRUITING: { varName: "--s-recruiting", label: "Recruiting" },
  NOT_YET_RECRUITING: { varName: "--s-notyet", label: "Not yet recruiting" },
  ENROLLING_BY_INVITATION: {
    varName: "--s-invite",
    label: "Enrolling by invitation",
  },
  ACTIVE_NOT_RECRUITING: { varName: "--s-active", label: "Active, not recruiting" },
  COMPLETED: { varName: "--s-completed", label: "Completed" },
  TERMINATED: { varName: "--s-terminated", label: "Terminated" },
  WITHDRAWN: { varName: "--s-terminated", label: "Withdrawn" },
  SUSPENDED: { varName: "--s-suspended", label: "Suspended" },
  UNKNOWN: { varName: "--s-unknown", label: "Unknown status" },
}

/** Legend display order — most-active first. Shared by the on-page timeline
 * and the export frame so the KEY renders identically on both surfaces. */
export const STATUS_ORDER = [
  "RECRUITING",
  "ACTIVE_NOT_RECRUITING",
  "ENROLLING_BY_INVITATION",
  "NOT_YET_RECRUITING",
  "COMPLETED",
  "TERMINATED",
  "WITHDRAWN",
  "SUSPENDED",
  "UNKNOWN",
]

/** Unique statuses present in a trial set, sorted by STATUS_ORDER. */
export function legendStatusesOf(
  statuses: (string | null | undefined)[],
): string[] {
  const seen: string[] = []
  statuses.forEach((s) => {
    const key = s || "UNKNOWN"
    if (!seen.includes(key)) seen.push(key)
  })
  return seen.sort(
    (a, b) => STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b),
  )
}

export function stMeta(s: string | null | undefined): StatusMeta {
  return (s && STATUS[s]) || STATUS.UNKNOWN
}

export function stColor(s: string | null | undefined): string {
  return `var(${stMeta(s).varName})`
}

export function isCompleted(s: string | null | undefined): boolean {
  return s === "COMPLETED"
}

export function parseD(s: string | null | undefined): Date | null {
  if (!s) return null
  const p = String(s).split("-")
  return new Date(+p[0], p[1] ? +p[1] - 1 : 0, p[2] ? +p[2] : 15)
}

export function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, d.getDate())
}

export function fmtMon(d: Date | null): string {
  return d ? d.toLocaleString("en-US", { month: "short", year: "numeric" }) : "n/a"
}

export function fmtDay(s: string | null | undefined): string | null {
  if (!s) return null
  const d = parseD(s)
  if (!d) return null
  return /^\d{4}-\d{2}-\d{2}$/.test(s)
    ? d.toLocaleString("en-US", { day: "numeric", month: "short", year: "numeric" })
    : `${fmtMon(d)} (month only)`
}

export function phShort(p: string | null | undefined): string {
  return String(p || "").replace(/PHASE/g, "P") || "n/a"
}

export function phLong(p: string | null | undefined): string {
  return (
    String(Array.isArray(p) ? p.join("/") : p || "")
      .replace(/PHASE/g, "Phase ")
      .replace(/_/g, " ") || "n/a"
  )
}

export function labelOf(t: PipelineTrial): string {
  return t.acronym || t.title || t.nctId
}

export type TaggedTrial = PipelineTrial & { _brand: string }

export function tagPartyTrials(
  trials: PipelineTrial[],
  brand: string,
  hideCompleted: boolean,
): TaggedTrial[] {
  return trials
    .filter((t) => t.startDate && t.primaryCompletionDate)
    .filter((t) => (hideCompleted ? !isCompleted(t.overallStatus) : true))
    .map((t) => ({ ...t, _brand: brand }))
    .sort(
      (a, b) =>
        (parseD(a.startDate)?.getTime() ?? 0) -
        (parseD(b.startDate)?.getTime() ?? 0),
    )
}

export function computeAxis(trials: TaggedTrial[], today: Date) {
  let min = today.getTime()
  let max = today.getTime()
  trials.forEach((t) => {
    const s = parseD(t.startDate)
    const p = parseD(t.primaryCompletionDate)
    if (s) min = Math.min(min, s.getTime())
    if (p) max = Math.max(max, addMonths(p, 9).getTime())
  })
  if (trials.length === 0) {
    const y = today.getFullYear()
    return { start: new Date(y - 2, 0, 1), end: new Date(y + 4, 0, 1) }
  }
  return {
    start: new Date(new Date(min).getFullYear(), 0, 1),
    end: new Date(new Date(max).getFullYear() + 1, 0, 1),
  }
}

export function pctOf(
  d: Date | null,
  axis: { start: Date; end: Date },
): number {
  if (!d) return 0
  return (
    ((d.getTime() - axis.start.getTime()) /
      (axis.end.getTime() - axis.start.getTime())) *
    100
  )
}

/** Stable 4dp % strings for inline styles (SSR/client hydration safe). */
export function fmtPct(value: number, floor = 0): string {
  const n = Number.isFinite(value) ? value : 0
  const rounded = Math.round(n * 10000) / 10000
  const clamped = floor > 0 ? Math.max(rounded, floor) : rounded
  return `${clamped}%`
}

/** Generic sublabel only when a distinct brand name exists. */
export function showGenericSublabel(brand: string, generic: string): boolean {
  return brand.trim().toLowerCase() !== generic.trim().toLowerCase()
}
